"use client";

/**
 * Section primitives for the dev landing pages. Deliberately no icon+text
 * card grids: features are told either as split sections with a real data
 * visual, or as borderless hairline text lists.
 */

/** Emerald-dot eyebrow label, Linear-style. */
export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
      <span className="text-xs uppercase tracking-[0.18em] text-[#8d918f]">{children}</span>
    </span>
  );
}

export interface FeaturePoint {
  /** Optional mono keyword shown before the text, e.g. an endpoint or field. */
  term?: string;
  text: React.ReactNode;
}

interface FeatureSplitProps {
  eyebrow: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  points?: FeaturePoint[];
  visual: React.ReactNode;
  /** Put the visual on the left on desktop. */
  flip?: boolean;
}

/**
 * Alternating feature section: copy on one side, a live-looking data visual
 * on the other, with an emerald glow bleeding out behind the visual.
 */
export function FeatureSplit({ eyebrow, title, lead, points, visual, flip }: FeatureSplitProps) {
  return (
    <div className="mt-20 grid items-center gap-10 sm:mt-24 lg:grid-cols-2 lg:gap-16">
      <div className={flip ? "lg:order-2" : ""}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h3 className="mt-4 max-w-md text-balance text-2xl tracking-tight text-white sm:text-3xl">{title}</h3>
        {lead ? <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/60">{lead}</p> : null}
        {points && points.length > 0 ? (
          <ul className="mt-7 max-w-md">
            {points.map((point, i) => (
              <li
                key={i}
                className={`flex flex-wrap items-baseline gap-x-2.5 gap-y-1 border-t border-white/[0.07] py-3.5 ${
                  i === points.length - 1 ? "border-b" : ""
                }`}
              >
                {point.term ? (
                  <span className="shrink-0 font-mono text-[13px] text-[#7fe7c8]">{point.term}</span>
                ) : null}
                <span className="min-w-0 flex-1 text-sm leading-relaxed text-white/60">{point.text}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className={`relative ${flip ? "lg:order-1" : ""}`}>
        <div
          className="pointer-events-none absolute -inset-10 rounded-full opacity-20"
          style={{
            background: "radial-gradient(ellipse, rgba(16,185,129,0.3) 0%, rgba(154,160,158,0.08) 45%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="relative">{visual}</div>
      </div>
    </div>
  );
}

export interface TextFeature {
  title: string;
  description: React.ReactNode;
}

/**
 * Borderless feature list: hairline-separated title + prose rows in up to two
 * columns. No boxes, no icons - the type does the work.
 */
export function TextFeatureList({ items }: { items: TextFeature[] }) {
  return (
    <div className="mx-auto mt-10 grid max-w-4xl gap-x-14 sm:grid-cols-2">
      {items.map((item) => (
        <div key={item.title} className="border-t border-white/[0.08] py-6">
          <h3 className="text-white">{item.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-white/60">{item.description}</p>
        </div>
      ))}
    </div>
  );
}
